// Copyright 2026. WebPros International GmbH. All rights reserved.

import { createCustomAction } from 'typesafe-actions';
import { Dispatch } from 'redux';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import {
    get,
    loadOnScroll,
    paginateList,
} from 'common/actions/actionsWrapper';
import { IPaginateApiResponse } from 'common/api/resources/Response';
import * as types from 'common/modules/failoverDomain/constants/types';
import {
    failoverDomains,
    IFailoverDomainCreateRequest,
    IFailoverDomainDetailResponse,
    IFailoverDomainPatchRequest,
    IFailoverDomainResponse,
} from 'common/api/resources/FailoverDomain';
import {
    setIsLoading,
    unsetIsLoading,
} from 'common/modules/app/loadingFlags/actions';
import { bakeForegroundToast } from 'common/modules/app/toaster/actions';
import { INTENT_TYPE } from 'common/constants';
import { HTTP_CODES } from 'common/api/constants';
import { IPaginatedWithSearch } from 'common/api/resources/Request/request';
import { computeResources } from 'common/api/resources/ComputeResource';
import { IComputeResourceResponse } from 'common/api/resources/ComputeResource/model';

export const setFailoverDomainList = createCustomAction(
    types.SET_FAILOVER_DOMAIN_LIST,
    (data: IPaginateApiResponse<IFailoverDomainResponse[]>) => ({ payload: data })
);

export const updateFailoverDomainItem = createCustomAction(
    types.UPDATE_FAILOVER_DOMAIN_ITEM,
    (data: IFailoverDomainResponse) => ({ payload: data })
);

export const setFailoverDomainItemIsLoading = createCustomAction(
    types.SET_FAILOVER_DOMAIN_ITEM_IS_LOADING,
    (id: number) => ({ payload: id })
);

export const unsetFailoverDomainItemIsLoading = createCustomAction(
    types.UNSET_FAILOVER_DOMAIN_ITEM_IS_LOADING,
    (id: number) => ({ payload: id })
);

export const removeFailoverDomainItem = createCustomAction(
    types.REMOVE_FAILOVER_DOMAIN_ITEM,
    (id: number) => ({ payload: id })
);

export const setFailoverDomainItem = createCustomAction(
    types.SET_FAILOVER_DOMAIN_ITEM,
    (data: IFailoverDomainDetailResponse) => ({ payload: data })
);

export const setFailoverDomainComputeResources = createCustomAction(
    types.SET_FAILOVER_DOMAIN_COMPUTE_RESOURCES,
    (data: IPaginateApiResponse<IComputeResourceResponse[]>) => ({ payload: data })
);

export const appendFailoverDomainComputeResources = createCustomAction(
    types.APPEND_FAILOVER_DOMAIN_COMPUTE_RESOURCES,
    (data: IPaginateApiResponse<IComputeResourceResponse[]>) => ({ payload: data })
);

export const getFailoverDomains = (params?: IPaginatedWithSearch) => async (dispatch: Dispatch) =>
    await paginateList({
        dispatch,
        loadingFlag: LOADING_FLAGS.FAILOVER_DOMAIN_LIST,
        action: setFailoverDomainList,
        apiCall: () => failoverDomains.list(params),
    });

export const getFailoverDomain = (id: number) => async (dispatch: Dispatch) =>
    await get(id, {
        dispatch,
        loadingFlag: LOADING_FLAGS.FAILOVER_DOMAIN_ITEM,
        action: setFailoverDomainItem,
        apiCall: failoverDomains.item,
    });

export const getFailoverDomainComputeResources = (
    failoverDomainId: number,
    params?: IPaginatedWithSearch
) => async (dispatch: Dispatch) =>
    await paginateList({
        dispatch,
        loadingFlag: LOADING_FLAGS.FAILOVER_DOMAIN_COMPUTE_RESOURCES,
        action: setFailoverDomainComputeResources,
        apiCall: () => computeResources.list({
            ...params,
            filters: { failover_domain_id: failoverDomainId },
        }),
    });

interface IScrollState {
    failoverDomain: { computeResources: IPaginateApiResponse<IComputeResourceResponse[]> };
    app: { loadingFlags: Set<LOADING_FLAGS> };
}

export const loadFailoverDomainComputeResourcesOnScroll = () =>
    async (dispatch: Dispatch, getState: () => IScrollState) => {
        const state = getState();
        await loadOnScroll({
            dispatch,
            loadingFlag: LOADING_FLAGS.FAILOVER_DOMAIN_COMPUTE_RESOURCES,
            action: appendFailoverDomainComputeResources,
            isLoading: state.app.loadingFlags.has(LOADING_FLAGS.FAILOVER_DOMAIN_COMPUTE_RESOURCES),
            nextPage: state.failoverDomain.computeResources.links.next,
        });
    };

interface IExcludeFromDomainState {
    failoverDomain: { item: IFailoverDomainDetailResponse };
}

// A CR leaves a failover domain by patching the domain with the reduced
// compute_resource_ids list; the backend FailoverDomainService then begins the
// HA-leave for the removed CR. SVM2-7262 removed the old per-CR
// `failover_domain_id` patch field in favour of this flow.
export const excludeFromDomain = (
    crId: number,
    failoverDomainId: number
) => async (dispatch: Dispatch, getState: () => IExcludeFromDomainState) => {
    const computeResourceIds = getState().failoverDomain.item.compute_resource_ids
        .filter((id) => id !== crId);

    try {
        await failoverDomains.patch(failoverDomainId, { compute_resource_ids: computeResourceIds });
    } catch (e) {
        // Non-422 failures are already toasted by the response interceptor; a 422 only lands in
        // formErrors, which nothing on the failover domain page renders — surface it here.
        if (e.response?.status !== HTTP_CODES.VALIDATION_ERROR) {
            throw e;
        }

        const [firstFieldErrors] = Object.values(e.response.data?.errors ?? {});
        const message = (Array.isArray(firstFieldErrors) ? firstFieldErrors[0] : undefined)
            ?? e.response.data?.message;
        if (message) {
            bakeForegroundToast(INTENT_TYPE.DANGER, message, undefined, false)(dispatch);
        } else {
            bakeForegroundToast(INTENT_TYPE.DANGER, 'failoverDomain.excludeError')(dispatch);
        }

        return;
    }

    await Promise.all([
        getFailoverDomainComputeResources(failoverDomainId)(dispatch),
        getFailoverDomain(failoverDomainId)(dispatch),
    ]);
};

export const toggleActiveState = (
    id: number,
    data: IFailoverDomainPatchRequest
) => async (dispatch: Dispatch) => {
    dispatch(setFailoverDomainItemIsLoading(id));

    try {
        const result = await failoverDomains.patch(id, data);
        dispatch(updateFailoverDomainItem(result.data.data));
        return result;
    } finally {
        dispatch(unsetFailoverDomainItemIsLoading(id));
    }
};

// The admin/computeResource updateIsLocked/updateIsInMaintenance thunks dispatch
// into state.computeResource, which the FD page doesn't read. Refetch the FD's
// CR list after the PATCH so the controlled Switch reflects the new value.
export const toggleCrIsLocked = (
    crId: number,
    isLocked: boolean,
    failoverDomainId: number
) => async (dispatch: Dispatch) => {
    await computeResources.patch(crId, { is_locked: isLocked });
    await getFailoverDomainComputeResources(failoverDomainId)(dispatch);
};

export const toggleCrIsInMaintenance = (
    crId: number,
    isInMaintenance: boolean,
    failoverDomainId: number
) => async (dispatch: Dispatch) => {
    await computeResources.patch(crId, { is_in_maintenance: isInMaintenance });
    await getFailoverDomainComputeResources(failoverDomainId)(dispatch);
};

export const deleteFailoverDomain = (id: number) => async (dispatch: Dispatch) => {
    dispatch(setFailoverDomainItemIsLoading(id));

    try {
        await failoverDomains.remove(id);
        dispatch(removeFailoverDomainItem(id));
        bakeForegroundToast(
            INTENT_TYPE.SUCCESS,
            'failoverDomain.toasts.failoverDomainDeleted'
        )(dispatch);
    } catch {
        bakeForegroundToast(
            INTENT_TYPE.DANGER,
            'failoverDomain.deleteError'
        )(dispatch);
    } finally {
        dispatch(unsetFailoverDomainItemIsLoading(id));
    }
};

export const createFailoverDomain = (
    data: IFailoverDomainCreateRequest
) => async (dispatch: Dispatch) => {
    dispatch(setIsLoading(LOADING_FLAGS.FAILOVER_DOMAIN_CREATE));

    try {
        const result = await failoverDomains.create(data);

        if ([HTTP_CODES.CREATED, HTTP_CODES.OK].includes(result.status)) {
            bakeForegroundToast(
                INTENT_TYPE.SUCCESS,
                'failoverDomain.toasts.failoverDomainSaved'
            )(dispatch);
        }

        return result;
    } finally {
        dispatch(unsetIsLoading(LOADING_FLAGS.FAILOVER_DOMAIN_CREATE));
    }
};

export const updateFailoverDomain = (
    id: number,
    data: IFailoverDomainPatchRequest
) => async (dispatch: Dispatch) => {
    dispatch(setIsLoading(LOADING_FLAGS.FAILOVER_DOMAIN_UPDATE));

    try {
        const result = await failoverDomains.patch(id, data);
        dispatch(updateFailoverDomainItem(result.data.data));

        if (result.status === HTTP_CODES.OK) {
            bakeForegroundToast(
                INTENT_TYPE.SUCCESS,
                'failoverDomain.toasts.failoverDomainSaved'
            )(dispatch);
        }

        return result;
    } finally {
        dispatch(unsetIsLoading(LOADING_FLAGS.FAILOVER_DOMAIN_UPDATE));
    }
};
